/**
 * Small decorative components that give the site its "joy" thread.
 * Use sparingly — they're meant to surprise, not dominate.
 */
import { Sparkles } from 'lucide-react';

/**
 * A hand-drawn underline that sits below a span of text.
 * Wrap any inline content like:  <JoyUnderline>joy</JoyUnderline>
 * The underline uses SVG so it scales with text size.
 */
export function JoyUnderline({ children, color = '#FCD968', strokeWidth = 7, className = '' }) {
  return (
    <span className={`relative inline-block ${className}`}>
      {children}
      <svg
        aria-hidden="true"
        viewBox="0 0 200 14"
        preserveAspectRatio="none"
        className="absolute left-0 -bottom-1 w-full h-[10px] pointer-events-none"
      >
        <path
          d="M2 8 C 40 2, 80 12, 120 6 S 198 4, 198 9"
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

/**
 * Sticker-style rotating tag. Drop into any layout for a playful accent.
 * Example: <JoySticker rotate={-6} color="coral">JOY</JoySticker>
 */
export function JoySticker({ children, rotate = -6, color = 'coral', className = '' }) {
  const colorMap = {
    coral: 'bg-[#E5614C] text-white',
    teal: 'bg-[#5DCAB5] text-white',
    yellow: 'bg-[#FCD968] text-[#2A1B1A]',
    dark: 'bg-[#2A1B1A] text-white',
    white: 'bg-white text-[#2A1B1A]',
  };
  return (
    <span
      className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] shadow-[0_8px_18px_-8px_rgba(42,27,26,0.4)] ${colorMap[color] || colorMap.coral} ${className}`}
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      {children}
    </span>
  );
}

/**
 * Sparkle accent for CTAs / headings. Subtle, animated.
 */
export function JoySparkle({ className = '', size = 16, color = '#FCD968' }) {
  return (
    <Sparkles
      className={`inline-block ${className}`}
      style={{ width: size, height: size, color }}
      strokeWidth={2.5}
      aria-hidden="true"
    />
  );
}

/**
 * Subtle confetti-dot field that sits behind hero / festival sections.
 * Pure CSS, no animation cost.
 */
export function JoyDotsField({ className = '' }) {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 opacity-[0.55] ${className}`}
      style={{
        backgroundImage:
          'radial-gradient(circle, #FCD968 1.5px, transparent 2px), radial-gradient(circle, #5DCAB5 1.5px, transparent 2px), radial-gradient(circle, #E5614C 1.5px, transparent 2px)',
        backgroundSize: '64px 64px, 96px 96px, 128px 128px',
        backgroundPosition: '0 0, 32px 32px, 16px 80px',
      }}
    />
  );
}
